import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, User, Shield, Users, FileText, ArrowLeft, ArrowRight, Home } from 'lucide-react';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

const roleDescriptions: Record<UserRole, { title: string; description: string; permissions: string[] }> = {
  creator: {
    title: 'Policy Creator',
    description: 'Create and manage draft policies',
    permissions: ['Create new policies', 'Edit draft policies', 'Submit for review']
  },
  reviewer: {
    title: 'Policy Reviewer',
    description: 'Review and provide feedback on policies',
    permissions: ['Review submitted policies', 'Add comments and suggestions', 'Approve or request changes']
  },
  approver: {
    title: 'Policy Approver',
    description: 'Final approval authority for policies',
    permissions: ['Approve reviewed policies', 'Publish approved policies', 'Archive outdated policies']
  },
  admin: {
    title: 'System Administrator',
    description: 'Full system access and user management',
    permissions: ['All creator, reviewer, and approver permissions', 'Manage users and roles', 'System configuration']
  },
  viewer: {
    title: 'Policy Viewer',
    description: 'View approved policies and use AI Agent',
    permissions: ['View approved policies only', 'Access AI Agent for questions', 'Read policy content']
  }
};

const getRoleIcon = (role: UserRole) => {
  switch (role) {
    case 'creator': return <FileText className="h-6 w-6" />;
    case 'reviewer': return <Users className="h-6 w-6" />;
    case 'approver': return <CheckCircle className="h-6 w-6" />;
    case 'admin': return <Shield className="h-6 w-6" />;
    case 'viewer': return <User className="h-6 w-6" />;
  }
};

const getRoleColor = (role: UserRole) => {
  switch (role) {
    case 'creator': return 'bg-primary text-primary-foreground';
    case 'reviewer': return 'bg-warning text-warning-foreground';
    case 'approver': return 'bg-success text-success-foreground';
    case 'admin': return 'bg-destructive text-destructive-foreground';
    case 'viewer': return 'bg-muted text-muted-foreground';
  }
};

const OnboardingPage = () => {
  const { profile, roles, company } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: `Welcome to ${company?.name || 'Manageaze Pulse'}, ${profile?.name}!`,
      content: (
        <div className="text-center space-y-4">
          <User className="h-16 w-16 mx-auto text-primary" />
          <p className="text-muted-foreground">
            {profile?.is_first_admin 
              ? "Congratulations! You're the first admin for your company. You have full access to manage policies and team members."
              : `Welcome to your company's policy management platform powered by Manageaze. You've been assigned specific roles to help manage organizational policies.`
            }
          </p>
        </div>
      )
    },
    {
      title: 'Your Role & Permissions',
      content: (
        <div className="space-y-6">
          {roles.length > 0 ? (
            roles.map((role) => {
              const roleInfo = roleDescriptions[role];
              return (
                <Card key={role} className="border-primary/20">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      {getRoleIcon(role)}
                      <div className="flex-1">
                        <CardTitle className="text-lg">{roleInfo.title}</CardTitle>
                        <CardDescription>{roleInfo.description}</CardDescription>
                      </div>
                      <Badge className={getRoleColor(role)}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Your permissions:</h4>
                      <ul className="space-y-1">
                        {roleInfo.permissions.map((permission, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            {permission}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="border-orange-200">
              <CardContent className="pt-6 text-center">
                <Shield className="h-12 w-12 mx-auto text-orange-500 mb-4" />
                <h3 className="font-semibold mb-2">Pending Role Assignment</h3>
                <p className="text-muted-foreground text-sm">
                  Your account is created but roles haven't been assigned yet. 
                  Please contact your administrator to assign appropriate roles.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )
    },
    {
      title: 'Platform Features',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <CardTitle className="text-lg">Policy Management</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Create, review, and manage organizational policies with our comprehensive workflow system.
                </p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Create new policies from scratch
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Upload existing policy documents
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Use AI-powered policy templates
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-green-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-green-600" />
                  <CardTitle className="text-lg">Team Collaboration</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Work together with your team through structured review and approval processes.
                </p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Assign roles and permissions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Track policy workflow status
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Monitor team activity
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-purple-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Shield className="h-6 w-6 text-purple-600" />
                  <CardTitle className="text-lg">Compliance Tracking</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Stay compliant with industry standards and regulations.
                </p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Monitor compliance status
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Generate compliance reports
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Set up automated reminders
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-orange-200">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <FileText className="h-6 w-6 text-orange-600" />
                  <CardTitle className="text-lg">AI Assistant</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Get help with policy creation and optimization using AI.
                </p>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    AI-powered policy suggestions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Content optimization
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Compliance recommendations
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },
    {
      title: 'Ready to Get Started!',
      content: (
        <div className="text-center space-y-4">
          <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
          <p className="text-muted-foreground">
            You're all set! You can now access the features available to your role. 
            Remember that access to certain features like draft policies depends on your assigned roles.
          </p>
          <div className="pt-4">
            <Link to="/">
              <Button className="gap-2">
                <Home className="h-4 w-4" />
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center space-x-2 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 rounded-full ${
                    index <= currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <CardTitle className="text-2xl">{steps[currentStep].title}</CardTitle>
            <CardDescription>
              Step {currentStep + 1} of {steps.length}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {steps[currentStep].content}
            
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button onClick={handleNext} disabled={currentStep === steps.length - 1} className="gap-2">
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
